import { SortButtonDropdown, SortItem } from '@/components/filters/SortButton';
import { Box, Button, Icon, type BoxProps, Popover, TextInput } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';

import { LeadFilters, LeadManagementFilterPopover } from './LeadManagementFilterPopover';

export const leadSortOptions: SortItem[] = [
  { id: 'locationAsc', label: 'Location A-Z' },
  { id: 'locationDesc', label: 'Location Z-A' },
  { id: 'systemSizeAsc', label: 'System Size Low-High' },
  { id: 'systemSizeDesc', label: 'System Size High-Low' },
  { id: 'batteryAsc', label: 'Battery Low-High' },
  { id: 'batteryDesc', label: 'Battery High-Low' },
  { id: 'pricePerWattAsc', label: 'Price Per Watt Low-High' },
  { id: 'pricePerWattDesc', label: 'Price Per Watt High-Low' },
  { id: 'statusDateAsc', label: 'Date Old-New' },
  { id: 'statusDateDesc', label: 'Date New-Old' },
];

interface LeadManagementFilterProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  activeFilterCount: number;
  currentFilters: LeadFilters;
  onApplyFilters: (filters: LeadFilters) => void;
  appliedSort: SortItem;
  onSortChange: (sort: SortItem) => void;
  onExport?: () => void;
  isExporting?: boolean;
}

const searchPrefix = <Icon name="search" />;
const searchWrapperStyle: React.CSSProperties = { width: '300px', flexShrink: 0 };
const popoverContentProps: BoxProps = {
  padding: '0',
  width: '340px',
  shadow: 'lg',
  radius: 'sm',
  overflow: 'hidden',
};

export const LeadManagementFilter = ({
  searchTerm,
  onSearchChange,
  activeFilterCount,
  currentFilters,
  onApplyFilters,
  appliedSort,
  onSortChange,
  onExport,
  isExporting = false,
}: LeadManagementFilterProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value),
    [onSearchChange],
  );

  const handleTogglePopover = useCallback(() => setIsPopoverOpen((prev) => !prev), []);
  const handleClosePopover = useCallback(() => setIsPopoverOpen(false), []);

  const handleApply = useCallback(
    (filters: LeadFilters) => {
      onApplyFilters(filters);
      setIsPopoverOpen(false);
    },
    [onApplyFilters],
  );

  const popoverContent = useMemo(
    () => (
      <LeadManagementFilterPopover
        currentFilters={currentFilters}
        onApply={handleApply}
        onClose={handleClosePopover}
      />
    ),
    [currentFilters, handleApply, handleClosePopover],
  );

  return (
    <Box
      direction="row"
      alignItems="center"
      radius="sm"
      padding="sm"
      background="grey-100"
      justifyContent="space-between"
    >
      <Box direction="row" alignItems="center" childGap="xs">
        <div style={searchWrapperStyle}>
          <TextInput
            id="lead-search"
            label="Search"
            hideLabel
            placeholder="Look up by address, system size, price..."
            value={searchTerm}
            onChange={handleSearchChange}
            prefix={searchPrefix}
            size="sm"
          />
        </div>
        <Popover
          content={popoverContent}
          isOpen={isPopoverOpen}
          placement="bottom-start"
          offsetFromTarget={4}
          contentContainerProps={popoverContentProps}
          hasArrow={false}
          onClickOutside={handleClosePopover}
          portalTarget={document.body}
          withPortal
        >
          <Button
            variant={activeFilterCount > 0 ? 'tertiary' : 'secondary'}
            tone="neutral"
            size="sm"
            iconPrefix="add"
            onClick={handleTogglePopover}
            aria-label="Filter"
          >
            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        </Popover>
      </Box>
      <Box direction="row" alignItems="center" childGap="xs">
        {onExport && (
          <Button
            variant="secondary"
            tone="neutral"
            size="sm"
            iconPrefix="download"
            onClick={onExport}
            isDisabled={isExporting}
            isLoading={isExporting}
          >
            Export
          </Button>
        )}
        <SortButtonDropdown appliedSort={appliedSort} sortOptions={leadSortOptions} onSortChange={onSortChange} />
      </Box>
    </Box>
  );
};
